import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SmilePlus } from "lucide-react";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥"];

interface EmojiReactionBarProps {
  messageId: string;
  reactions?: Array<{ emoji: string; userIds: string[]; count: number }>;
  currentUserId: string;
  onReact: (messageId: string, emoji: string) => void;
  onUnreact: (messageId: string, emoji: string) => void;
}

/**
 * Quick emoji reaction picker — appears on hover.
 * Also renders reaction pills below the message.
 */
export function EmojiReactionBar({
  messageId,
  reactions = [],
  currentUserId,
  onReact,
  onUnreact,
}: EmojiReactionBarProps) {
  const [showPicker, setShowPicker] = useState(false);

  const handleEmojiClick = (emoji: string) => {
    const existing = reactions.find((r) => r.emoji === emoji);
    if (existing?.userIds.includes(currentUserId)) {
      onUnreact(messageId, emoji);
    } else {
      onReact(messageId, emoji);
    }
    setShowPicker(false);
  };

  return (
    <div className="relative">
      {/* Reaction pills — always visible when there are reactions */}
      {reactions.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {reactions.map((r) => {
            const isOwn = r.userIds.includes(currentUserId);
            return (
              <button
                key={r.emoji}
                onClick={() => handleEmojiClick(r.emoji)}
                className={`
                  inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                  transition-all duration-150
                  ${isOwn
                    ? "bg-[var(--accent)]/20 border border-[var(--accent)]/40 text-[var(--accent)]"
                    : "bg-muted/50 border border-border/50 text-muted-foreground hover:bg-muted"
                  }
                `}
              >
                <span>{r.emoji}</span>
                <span className="font-medium">{r.count}</span>
              </button>
            );
          })}

          {/* + button to open picker */}
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted/30 border border-border/30 text-muted-foreground hover:bg-muted transition-colors"
          >
            +
          </button>
        </div>
      )}

      {/* Floating emoji picker */}
      <AnimatePresence>
        {showPicker && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full mb-1 left-0 z-20 flex items-center gap-1 px-2 py-1.5 rounded-full shadow-lg border border-border/50 backdrop-blur-md"
            style={{ background: 'var(--header-bg)' }}
          >
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors text-lg hover:scale-125 transform duration-100"
              >
                {emoji}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* React trigger when no reactions exist yet. On mobile there's no
          hover, so the trigger needs to be tappable without first hovering.
          Desktop: faded by default, becomes opaque on bubble hover; mobile
          (no hover): always at the desktop hover-opacity. */}
      {reactions.length === 0 && (
        <button
          onClick={() => setShowPicker(!showPicker)}
          aria-label="Add reaction"
          className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-muted/40 border border-border/40 px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all opacity-60 group-hover:opacity-100 sm:opacity-30"
        >
          <SmilePlus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
